import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  confirmEmailOtp,
  getMe,
  getSession,
  resendEmailOtp,
  saveSession,
} from '@/domains/auth/services/auth.service';
import { getApiErrorMessage } from '@/utils/apiErrors';

const INPUT =
  'w-full px-4 py-3 bg-slate-50/50 border border-slate-200 rounded-xl text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:bg-white focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 transition-all tracking-[0.35em] text-center font-semibold';

export default function VerifyEmailForm() {
  const navigate = useNavigate();
  const session = getSession();
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [hint, setHint] = useState('');
  const [error, setError] = useState('');
  const [done, setDone] = useState(session?.user?.email_verified === true);

  const email = session?.user?.email ?? '';

  const handleResend = async () => {
    setError('');
    setLoading(true);
    try {
      const res = await resendEmailOtp();
      const debug =
        import.meta.env.DEV && res.debug_otp_code
          ? ` (dev: ${res.debug_otp_code})`
          : '';
      setHint(`Code renvoyé${debug}`);
    } catch (e) {
      setError(getApiErrorMessage(e, "Impossible d'envoyer le code."));
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async (e: React.FormEvent) => {
    e.preventDefault();
    if (code.length !== 6) {
      setError('Le code doit contenir 6 chiffres.');
      return;
    }
    setError('');
    setLoading(true);
    try {
      await confirmEmailOtp(code);
      const user = await getMe();
      const token = session?.token ?? localStorage.getItem('auth_token') ?? '';
      if (token) {
        saveSession({ token, token_type: 'Bearer', user });
      }
      setDone(true);
      setTimeout(() => navigate(session?.dashboardPath || '/'), 1200);
    } catch (err) {
      setError(getApiErrorMessage(err, 'Code invalide.'));
    } finally {
      setLoading(false);
    }
  };

  if (!session) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
        <p className="text-sm text-slate-600">
          Connectez-vous d&apos;abord.{' '}
          <Link to="/login" className="text-teal-600 font-semibold">Connexion</Link>
        </p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 font-sans flex flex-col antialiased">
      <header className="bg-white border-b border-slate-100 sticky top-0 z-50 shadow-sm px-4 h-16 flex items-center">
        <div className="max-w-6xl w-full mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center space-x-3">
            <div className="w-9 h-9 bg-teal-500 rounded-xl flex items-center justify-center text-slate-950 font-black text-lg">
              O
            </div>
            <span className="font-bold text-base tracking-tight">ONAPILA</span>
          </Link>
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center px-4 py-10">
        <div className="w-full max-w-md bg-white rounded-2xl border border-slate-100 shadow-md p-6 sm:p-8">
          <div className="w-12 h-12 rounded-xl bg-teal-50 text-teal-600 flex items-center justify-center mb-4">
            <i className="ri-mail-check-line text-xl" />
          </div>
          <h1 className="text-xl font-extrabold tracking-tight">Vérifier mon e-mail</h1>
          <p className="text-sm text-slate-500 mt-1.5 leading-relaxed">
            {done
              ? 'Votre adresse e-mail est confirmée.'
              : <>Confirmez <span className="font-medium text-slate-700">{email || 'votre adresse'}</span> pour recevoir les notifications importantes.</>}
          </p>

          {done ? (
            <p className="mt-6 text-sm text-emerald-700 flex items-center gap-1.5">
              <i className="ri-checkbox-circle-fill" />
              E-mail vérifié — redirection…
            </p>
          ) : (
            <form onSubmit={handleConfirm} className="mt-6 space-y-4">
              <input
                type="text"
                inputMode="numeric"
                maxLength={6}
                value={code}
                onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="000000"
                className={INPUT}
              />
              {hint && <p className="text-xs text-slate-500">{hint}</p>}
              {error && <p className="text-xs text-red-500">{error}</p>}
              <button
                type="submit"
                disabled={loading || code.length !== 6}
                className="w-full py-3 rounded-xl bg-teal-500 text-slate-950 font-bold text-sm disabled:opacity-50"
              >
                {loading ? 'Vérification…' : 'Confirmer'}
              </button>
              <button
                type="button"
                onClick={handleResend}
                disabled={loading}
                className="w-full text-sm text-teal-600 font-semibold hover:underline disabled:opacity-50"
              >
                Renvoyer le code
              </button>
            </form>
          )}
        </div>
      </main>
    </div>
  );
}
